// src/utils/staleVersion.js
// Rechargement silencieux quand l'élève fait tourner une ancienne version du site.
// sessionMonitor détecte la version périmée (markStaleVersion) ; StaleVersionReloader (App.js) recharge
// au changement de page, jamais en plein exercice. Garde-fou : un seul rechargement par build cible.
import { hasPendingXpCalls } from './xpCallQueue';
import MonitoringService from '../services/MonitoringService';
import { auth } from '../firebaseConfig';
import { deviceInfo, buildId } from './staveRenderMonitor';

const FLAG_KEY = 'drumigo-reloaded-for';
const CHUNK_KEY = 'drumigo-chunk-reload-at';
const PENDING_KEY = 'drumigo-reload-pending';
const WAIT_XP_MAX_MS = 10000;

let staleLatest = null;

export function markStaleVersion(latest) { staleLatest = latest; }

function sessionGet(key) { try { return sessionStorage.getItem(key); } catch { return null; } }
function sessionSet(key, v) { try { sessionStorage.setItem(key, v); } catch { /* ignore */ } }

// Un événement envoyé juste avant reload() risquerait d'être perdu : on écrit un marqueur, et c'est la page
// rechargée qui rapporte le résultat (reportPendingReload, appelé par sessionMonitor une fois l'élève connecté).
function recordPendingReload(reason, target) {
  sessionSet(PENDING_KEY, JSON.stringify({ reason, from: buildId(), target: target || null, page: window.location.pathname, at: Date.now() }));
}

export function reportPendingReload(uid) {
  try {
    const raw = sessionGet(PENDING_KEY);
    if (!raw) return;
    try { sessionStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
    const p = JSON.parse(raw);
    const running = buildId();
    // Cible connue (version périmée) : on doit tourner sur elle. Chunk : le build doit avoir changé.
    const ok = p.target ? running === p.target : running !== p.from;
    const params = {
      exerciseId: `${p.reason}|${p.from}->${running}`,
      reason: p.reason, from: p.from, target: p.target, running, ok,
      delayMs: Date.now() - p.at,
      page: p.page,
      ...deviceInfo(),
    };
    if (ok) MonitoringService.info('app_forced_reload', params, uid);
    else MonitoringService.warning('app_forced_reload_failed', params, uid); // le rechargement n'a pas servi : risque de boucle
  } catch { /* le monitoring ne casse jamais l'app */ }
}

function reportDeferred(target) {
  try {
    if (sessionGet('drumigo-reload-deferred-logged')) return;
    sessionSet('drumigo-reload-deferred-logged', '1');
    MonitoringService.info('app_forced_reload_deferred', {
      exerciseId: `xp-pending|${target}`,
      reason: 'xp_pending', target, running: buildId(), page: window.location.pathname, ...deviceInfo(),
    }, auth.currentUser?.uid || null);
  } catch { /* ignore */ }
}

// À appeler à chaque changement de page. Renvoie true si un rechargement a été programmé.
export function reloadIfStale() {
  if (!staleLatest || sessionGet(FLAG_KEY) === staleLatest) return false;
  const target = staleLatest;
  const startedAt = Date.now();
  const tryReload = () => {
    if (hasPendingXpCalls() && Date.now() - startedAt < WAIT_XP_MAX_MS) {
      setTimeout(tryReload, 500); // laisse partir l'XP en cours (sinon perdu)
      return;
    }
    if (hasPendingXpCalls()) { reportDeferred(target); return; } // toujours occupé : on retentera à la prochaine navigation
    sessionSet(FLAG_KEY, target);
    recordPendingReload('stale_version', target);
    window.location.reload();
  };
  tryReload();
  return true;
}

// Chunk JS introuvable (vieille version après un déploiement) : recharger une fois, pas en boucle.
export function reloadOnChunkError() {
  const last = Number(sessionGet(CHUNK_KEY) || 0);
  if (Date.now() - last < 60000) return false;
  sessionSet(CHUNK_KEY, String(Date.now()));
  recordPendingReload('chunk_error', null);
  window.location.reload();
  return true;
}
